import { Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { catchError, map, switchMap, of, tap } from 'rxjs'
import { casesActions } from './cases.actions'
import { DatasetBrowserApiService } from '../../shared/services/dataset-browser-api.service'
import { ToasterService } from '../../shared/services/toaster.service'

@Injectable()
export class CasesEffects {
    constructor(
        private readonly actions$: Actions,
        private readonly datasetBrowserApi: DatasetBrowserApiService,
        private readonly toaster: ToasterService
    ) {}

    loadCases$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                casesActions.loadCases,
                casesActions.creatingFolderSucceeded,
                casesActions.creatingDatasetSucceeded
            ),
            switchMap(() => this.datasetBrowserApi.getFolders()),
            map(cases => casesActions.casesLoaded({ cases }))
        )
    })

    deleteFolder$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(casesActions.deleteFolder),
            switchMap(({ folderId }) =>
                this.datasetBrowserApi.deleteFolder(folderId).pipe(
                    map(() => casesActions.deletingFolderSucceeded()),
                    catchError(error => {
                        return of(
                            casesActions.deletingFolderFailed({
                                error,
                                message: 'Error deleting folder.',
                            })
                        )
                    })
                )
            )
        )
    })

    moveFolder$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(casesActions.moveFolder),
            switchMap(({ folderId, parentId }) =>
                this.datasetBrowserApi.moveFolder(folderId, parentId).pipe(
                    map(() => casesActions.movingFolderSucceeded()),
                    catchError(error => {
                        return of(
                            casesActions.movingFolderFailed({
                                error,
                                message: 'Error moving folder.',
                            })
                        )
                    })
                )
            )
        )
    })

    createFolder$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(casesActions.createFolder),
            switchMap(({ name, parentId }) =>
                this.datasetBrowserApi.createFolder(name, parentId).pipe(
                    map(() => casesActions.creatingFolderSucceeded({ parentId })),
                    catchError(error => {
                        return of(
                            casesActions.creatingFolderFailed({
                                error,
                                message: 'Error creating folder.',
                            })
                        )
                    })
                )
            )
        )
    })

    moveDataset$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(casesActions.moveDataset),
            switchMap(({ datasetId, parentId }) =>
                this.datasetBrowserApi.moveDataset(datasetId, parentId).pipe(
                    map(() => casesActions.movingDatasetSucceeded()),
                    catchError(error => {
                        return of(
                            casesActions.movingDatasetFailed({
                                error,
                                message: 'Error moving dataset.',
                            })
                        )
                    })
                )
            )
        )
    })

    deleteDataset$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(casesActions.deleteDataset),
            switchMap(({ datasetId }) =>
                this.datasetBrowserApi.deleteDataset(datasetId).pipe(
                    map(() => casesActions.deletingDatasetSucceeded()),
                    catchError(error => {
                        return of(
                            casesActions.deletingDatasetFailed({
                                error,
                                message: 'Error deleting dataset.',
                            })
                        )
                    })
                )
            )
        )
    })

    showDatasetHandlingErrorToast$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                casesActions.movingDatasetFailed,
                casesActions.deletingDatasetFailed,
                casesActions.creatingFolderFailed,
                casesActions.movingFolderFailed,
                casesActions.deletingFolderFailed
            ),
            tap(({ error, message }) => {
                this.toaster.httpError(error, message)
            }),
            map(() => casesActions.loadCases())
        )
    })
}
